import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useMembers } from "../../../hooks/useMembers";

export const useAttendanceSearch = ({ isOpen, idTenant, members, setMembers }) => {
    const { members: allMembers } = useMembers();
    const [searchText, setSearchText] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearchingExtra, setIsSearchingExtra] = useState(false);
    const [justAddedId, setJustAddedId] = useState(null);
    const searchDebounceRef = useRef(null);
    const activeMembers = useMemo(() => (
        (allMembers || []).filter(member => member.status === "active")
    ), [allMembers]);

    useEffect(() => {
        if (!isOpen || !idTenant) {
            setSearchResults([]);
            return;
        }
    }, [isOpen, idTenant]);

    useEffect(() => {
        if (!isOpen) return;

        clearTimeout(searchDebounceRef.current);

        const trimmed = searchText.trim();
        if (trimmed.length < 2) {
            setSearchResults([]);
            return;
        }

        searchDebounceRef.current = setTimeout(() => {
            setIsSearchingExtra(true);
            try {
                const queryLower = trimmed.toLowerCase();
                const queryNorm = queryLower.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                const alreadyInList = new Set(members.map(m => m.id));

                const filtered = activeMembers
                    .filter(m => !alreadyInList.has(m.id))
                    .filter(m => {
                        const fullName = (m.fullName || "").toLowerCase();
                        const fullNameNorm = fullName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                        const searchName = (m.searchName || "").toLowerCase();
                        const memberNum = String(m.memberNumber || "");

                        return fullName.includes(queryLower)
                            || fullNameNorm.includes(queryNorm)
                            || searchName.includes(queryNorm)
                            || memberNum.includes(trimmed);
                    })
                    .slice(0, 8)
                    .map(m => ({ id: m.id, name: m.fullName, memberNumber: m.memberNumber, photo: m.photoUrl, memberStatus: m.status }));

                setSearchResults(filtered);
            } catch (err) {
                console.error("[useAttendanceSearch] Erro na busca de membros:", err);
                setSearchResults([]);
            } finally {
                setIsSearchingExtra(false);
            }
        }, 300);

        return () => clearTimeout(searchDebounceRef.current);
    }, [searchText, isOpen, members, activeMembers]);

    // Reseta no fechar modal
    useEffect(() => {
        if (!isOpen) {
            setSearchText("");
            setSearchResults([]);
            setJustAddedId(null);
        }
    }, [isOpen]);

    const handleSelectSearchMember = useCallback((selectedMember) => {
        setMembers(prev => {
            if (prev.some(m => m.id === selectedMember.id)) return prev;
            return [
                ...prev,
                {
                    id: selectedMember.id,
                    name: selectedMember.name,
                    photo: selectedMember.photo || null,
                    memberStatus: selectedMember.memberStatus,
                    idMember: selectedMember.memberNumber,
                    enrollmentType: "extra",
                    idEnrollment: null,
                    justification: "",
                    status: "present",
                    initialFrontendStatus: "present",
                    oldStatus: "BOOKED",
                    oldJustification: "",
                },
            ];
        });
        setJustAddedId(selectedMember.id);
        setSearchText("");
        setSearchResults([]);
        setTimeout(() => setJustAddedId(null), 2500);
    }, [setMembers]);

    return {
        searchText,
        setSearchText,
        searchResults,
        isSearchingExtra,
        justAddedId,
        handleSelectSearchMember
    };
};
